import React, { useRef, useEffect, useState } from 'react';
import { useFOSProcessingLog, FOSLogEntry } from '../../../hooks/useFOSProcessingLog';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import IconWrapper from '../../ui/IconWrapper';
import {
  FiChevronDown,
  FiChevronUp,
  FiTrash2,
  FiActivity,
  FiCheckCircle,
  FiXCircle,
  FiLoader,
} from 'react-icons/fi';

interface FOSProcessingLogFeedProps {
  maxHeight?: string;
  defaultExpanded?: boolean;
  maxEntries?: number;
}

type FilterType = 'all' | 'completed' | 'failed' | 'in-progress';

const getEntryIcon = (entry: FOSLogEntry) => {
  switch (entry.type) {
    case 'completed':
      return <IconWrapper icon={FiCheckCircle} className="h-4 w-4 text-green-500" />;
    case 'failed':
      return <IconWrapper icon={FiXCircle} className="h-4 w-4 text-red-500" />;
    case 'started':
    case 'progress':
      return <IconWrapper icon={FiLoader} className="h-4 w-4 text-blue-500 animate-spin" />;
    default:
      return <IconWrapper icon={FiActivity} className="h-4 w-4 text-gray-400" />;
  }
};

const getEntryColor = (entry: FOSLogEntry): string => {
  switch (entry.type) {
    case 'completed':
      return 'text-green-700';
    case 'failed':
      return 'text-red-700';
    case 'started':
    case 'progress':
      return 'text-blue-700';
    default:
      return 'text-gray-600';
  }
};

const formatTime = (date: Date): string => {
  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const FOSProcessingLogFeed: React.FC<FOSProcessingLogFeedProps> = ({
  maxHeight = '300px',
  defaultExpanded = false,
  maxEntries = 100,
}) => {
  const { entries, isProcessing, clearLog, stats } = useFOSProcessingLog({ maxEntries });
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [filter, setFilter] = useState<FilterType>('all');
  const logContainerRef = useRef<HTMLDivElement>(null);

  // Auto-expand when processing starts
  useEffect(() => {
    if (isProcessing && !isExpanded) {
      setIsExpanded(true);
    }
  }, [isProcessing, isExpanded]);

  // Filter entries based on selected filter
  const filteredEntries = entries.filter(entry => {
    switch (filter) {
      case 'completed':
        return entry.type === 'completed';
      case 'failed':
        return entry.type === 'failed';
      case 'in-progress':
        return entry.type === 'started' || entry.type === 'progress';
      default:
        return true;
    }
  });

  return (
    <Card className={isProcessing ? 'border-blue-200' : ''}>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                <div className={`p-2 rounded-full ${isProcessing ? 'bg-blue-100' : 'bg-gray-100'}`}>
                  <IconWrapper
                    icon={FiActivity}
                    className={`h-5 w-5 ${isProcessing ? 'text-blue-600 animate-pulse' : 'text-gray-600'}`}
                  />
                </div>
                <div className="text-left">
                  <CardTitle className="text-base flex items-center gap-2">
                    Processing Log
                    {entries.length > 0 && (
                      <Badge variant="outline" className="ml-2">
                        {entries.length}
                      </Badge>
                    )}
                  </CardTitle>
                  <p className="text-xs text-gray-500">
                    {isProcessing ? 'Processing in progress...' : 'Real-time activity feed'}
                  </p>
                </div>
                <IconWrapper
                  icon={isExpanded ? FiChevronUp : FiChevronDown}
                  className="h-4 w-4 text-gray-400 ml-2"
                />
              </button>
            </CollapsibleTrigger>

            <div className="flex items-center gap-2">
              {/* Quick stats */}
              {entries.length > 0 && (
                <div className="flex items-center gap-3 text-xs mr-2">
                  {stats.completed > 0 && (
                    <span className="flex items-center gap-1 text-green-600">
                      <IconWrapper icon={FiCheckCircle} className="h-3 w-3" />
                      {stats.completed}
                    </span>
                  )}
                  {stats.failed > 0 && (
                    <span className="flex items-center gap-1 text-red-600">
                      <IconWrapper icon={FiXCircle} className="h-3 w-3" />
                      {stats.failed}
                    </span>
                  )}
                  {stats.inProgress > 0 && (
                    <span className="flex items-center gap-1 text-blue-600">
                      <IconWrapper icon={FiLoader} className="h-3 w-3 animate-spin" />
                      {stats.inProgress}
                    </span>
                  )}
                </div>
              )}

              {/* Clear button */}
              {entries.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearLog();
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <IconWrapper icon={FiTrash2} className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="pt-0">
            {/* Filter dropdown */}
            {entries.length > 0 && (
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
                <span className="text-xs text-gray-500">
                  Showing {filteredEntries.length} of {entries.length} entries
                </span>
                <Select value={filter} onValueChange={(v) => setFilter(v as FilterType)}>
                  <SelectTrigger className="w-[130px] h-8 text-xs">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="in-progress">In Progress</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Log entries */}
            <div
              ref={logContainerRef}
              className="overflow-y-auto font-mono text-xs"
              style={{ maxHeight }}
            >
              {filteredEntries.length === 0 ? (
                <div className="text-center py-6 text-gray-400">
                  {filter === 'all'
                    ? 'No processing activity yet'
                    : `No ${filter} entries`}
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredEntries.map((entry) => (
                    <div
                      key={entry.id}
                      className={`flex items-start gap-2 py-1.5 px-2 rounded hover:bg-gray-50 ${
                        entry.type === 'failed' ? 'bg-red-50' : ''
                      }`}
                    >
                      <span className="text-gray-400 shrink-0 tabular-nums">
                        {formatTime(entry.timestamp)}
                      </span>
                      <span className="shrink-0">{getEntryIcon(entry)}</span>
                      <span className={`flex-1 ${getEntryColor(entry)}`}>
                        {entry.message}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Processing indicator */}
            {isProcessing && (
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-xs text-blue-600">
                <IconWrapper icon={FiLoader} className="h-3 w-3 animate-spin" />
                Processing decisions...
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default FOSProcessingLogFeed;
